import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Lock,
  Eye,
  EyeOff,
  Check,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Loader2,
  Shield,
} from "lucide-react";
import mantisLogo from "../Assets/img/mantisBlackLogo.png";
import sandyLogo from "../Assets/img/sandyBlackLogo.png";
import { brand } from "../config";
import { resetPassword } from "../ApiCalls/mantisBusiness";

const MIN_PASSWORD_LENGTH = 10;

function ForgotPasswordBusiness() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordConfirmed, setPasswordConfirmed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { token } = useParams();
  const navigate = useNavigate();

  // Validaciones en tiempo real
  const hasMinLength = password.length >= MIN_PASSWORD_LENGTH;
  const hasLetterAndNumber = /[a-zA-Z]/.test(password) && /[0-9]/.test(password);
  const passwordsMatch = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;
  const isFormValid = hasMinLength && hasLetterAndNumber && passwordsMatch;

  // Medidor de fortaleza de contraseña
  const passwordStrength = useMemo(() => {
    if (!password) return { score: 0, label: "", color: "bg-slate-200" };
    let score = 0;
    if (password.length >= 10) score += 1;
    if (password.length >= 14) score += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;

    if (score <= 2) return { score: 33, label: "Básica", color: "bg-amber-400" };
    if (score <= 3) return { score: 66, label: "Buena", color: "bg-orange-500" };
    return { score: 100, label: "Excelente", color: "bg-emerald-500" };
  }, [password]);

  async function handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    setErrorMessage("");

    if (!token) {
      setErrorMessage("Token de recuperación no proporcionado.");
      return;
    }

    if (!hasMinLength) {
      setErrorMessage(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }

    if (!hasLetterAndNumber) {
      setErrorMessage("La contraseña debe incluir al menos una letra y un número.");
      return;
    }

    if (!passwordsMatch) {
      setErrorMessage("Las contraseñas ingresadas no coinciden.");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await resetPassword(token, password);
      if (res && res.success) {
        setPasswordConfirmed(true);
      } else {
        setErrorMessage(
          res?.message ||
            "El enlace de recuperación es inválido o ha expirado. Por favor solicita uno nuevo."
        );
      }
    } catch (err) {
      console.error("Error al restablecer contraseña:", err);
      setErrorMessage("Error de conexión al servidor. Inténtalo más tarde.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="h-screen max-h-screen bg-[#f8fafc] flex items-center justify-center p-3 sm:p-4 relative overflow-hidden">
      {/* Background Ambient Lights */}
      <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-gradient-to-br from-orange-200/25 to-transparent blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-gradient-to-tl from-amber-200/25 to-transparent blur-3xl pointer-events-none"></div>

      <div className="w-full max-w-md mx-auto relative z-10">
        <div className="bg-white rounded-[28px] border border-slate-200/80 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)] p-6 sm:p-7 relative overflow-hidden">
          {/* Top Accent Gradient Bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-400"></div>

          {/* Logo Header */}
          <div className="text-center mb-4">
            <img
              src={brand === "sandy" ? sandyLogo : mantisLogo}
              alt="Logo"
              className="h-10 sm:h-12 w-auto mx-auto object-contain drop-shadow-sm"
            />
          </div>

          {!passwordConfirmed ? (
            <div className="space-y-4">
              {/* Header Info */}
              <div className="text-center space-y-1.5">
                <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-orange-50 text-orange-700 border border-orange-200/70 shadow-sm mx-auto">
                  <Shield className="w-3 h-3 text-orange-600" />
                  <span>Protección de Acceso</span>
                </div>

                <div className="space-y-0.5">
                  <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                    Nueva Contraseña
                  </h1>
                  <p className="text-xs text-slate-500 max-w-xs mx-auto leading-relaxed">
                    Crea una clave segura para acceder a tu cuenta empresarial.
                  </p>
                </div>
              </div>

              {/* Error Alert */}
              {errorMessage && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-2.5 flex items-start space-x-2 text-red-700 text-xs shadow-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5 text-red-500" />
                  <div className="flex-1">
                    <p className="font-semibold text-red-800">No se pudo restablecer</p>
                    <p className="text-red-600 text-[11px] mt-0.5">{errorMessage}</p>
                    <button
                      type="button"
                      onClick={() => navigate("/")}
                      className="mt-1 text-[11px] font-bold text-red-700 underline hover:text-red-900 block"
                    >
                      Volver al inicio de sesión →
                    </button>
                  </div>
                </div>
              )}

              {/* Reset Password Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* New Password Field */}
                <div className="space-y-1 text-left">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    Nueva Contraseña *
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-orange-500 transition-colors">
                      <Lock className="h-4 w-4" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Mínimo 10 caracteres con letras y números"
                      className="w-full pl-9 pr-10 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200 focus:border-orange-500 rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-2 focus:ring-orange-500/10 transition-all outline-none shadow-sm"
                      disabled={isSubmitting}
                      required
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                      disabled={isSubmitting}
                      aria-label="Alternar visibilidad"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {password.length > 0 && (
                    <div className="space-y-0.5 pt-0.5">
                      <div className="flex justify-between text-[10px] text-slate-500">
                        <span>Seguridad:</span>
                        <span className="font-semibold text-slate-700">{passwordStrength.label}</span>
                      </div>
                      <div className="h-1 w-full bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${passwordStrength.color} transition-all duration-300 rounded-full`}
                          style={{ width: `${passwordStrength.score}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="space-y-1 text-left">
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                    Confirmar Contraseña *
                  </label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 group-focus-within:text-orange-500 transition-colors">
                      <ShieldCheck className="h-4 w-4" />
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Repite tu nueva contraseña"
                      className={`w-full pl-9 pr-10 py-2.5 bg-slate-50 hover:bg-white focus:bg-white border rounded-xl text-slate-900 placeholder-slate-400 text-xs sm:text-sm font-medium focus:ring-2 focus:ring-orange-500/10 transition-all outline-none shadow-sm ${
                        confirmPassword && password !== confirmPassword
                          ? "border-red-400 focus:border-red-500"
                          : "border-slate-200 focus:border-orange-500"
                      }`}
                      disabled={isSubmitting}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1"
                      disabled={isSubmitting}
                      aria-label="Alternar visibilidad"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Password Requirements Checklist */}
                <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-2.5 space-y-1 text-left text-xs">
                  <div className="flex items-center gap-1.5">
                    {hasMinLength ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full border border-slate-300 flex-shrink-0 flex items-center justify-center">
                        <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                      </div>
                    )}
                    <span className={hasMinLength ? "text-emerald-700 font-semibold" : "text-slate-600"}>
                      Mínimo 10 caracteres
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {hasLetterAndNumber ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full border border-slate-300 flex-shrink-0 flex items-center justify-center">
                        <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                      </div>
                    )}
                    <span className={hasLetterAndNumber ? "text-emerald-700 font-semibold" : "text-slate-600"}>
                      Al menos una letra y un número
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {passwordsMatch ? (
                      <Check className="h-3.5 w-3.5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full border border-slate-300 flex-shrink-0 flex items-center justify-center">
                        <div className="w-1 h-1 rounded-full bg-slate-300"></div>
                      </div>
                    )}
                    <span className={passwordsMatch ? "text-emerald-700 font-semibold" : "text-slate-600"}>
                      Las dos contraseñas coinciden
                    </span>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-1">
                  <button
                    type="submit"
                    disabled={isSubmitting || !isFormValid}
                    className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 shadow-md shadow-orange-500/25 hover:shadow-lg transform hover:-translate-y-0.5 active:translate-y-0 text-xs sm:text-sm"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Actualizando contraseña...</span>
                      </>
                    ) : (
                      <>
                        <span>Actualizar Contraseña</span>
                        <ArrowRight className="h-4 w-4" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            /* Success View */
            <div className="text-center space-y-4 py-2 animate-in fade-in zoom-in-95 duration-300">
              <div className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold tracking-wider uppercase bg-emerald-50 text-emerald-700 border border-emerald-200/70 shadow-sm mx-auto">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                <span>Cambio Completado</span>
              </div>

              <div className="relative mx-auto w-14 h-14 rounded-2xl bg-gradient-to-tr from-emerald-600 via-emerald-500 to-teal-400 flex items-center justify-center text-white shadow-lg shadow-emerald-500/25 ring-4 ring-emerald-500/10">
                <Check className="h-7 w-7 stroke-[2.5]" />
              </div>

              <div className="space-y-1">
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
                  ¡Contraseña Actualizada!
                </h2>
                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-xs mx-auto">
                  Tu contraseña empresarial se ha actualizado con éxito.
                </p>
              </div>

              <div className="pt-2">
                <button
                  type="button"
                  onClick={() => navigate("/")}
                  className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white py-3 px-5 rounded-xl font-bold transition-all duration-200 shadow-md shadow-orange-500/25 hover:shadow-lg flex items-center justify-center space-x-2 text-xs sm:text-sm"
                >
                  <span>Iniciar Sesión</span>
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordBusiness;
